<template>
  <div class="gallery-container">
    <!-- Main Image Display -->
    <div class="main-image-container relative mb-4">
      <div class="relative overflow-hidden rounded-lg">
        <img 
          :src="images[currentIndex].largeURL" 
          :alt="images[currentIndex].caption || `Image ${currentIndex + 1}`"
          class="main-image w-full h-auto max-h-96 md:max-h-[500px] object-contain cursor-pointer transition-transform duration-300 hover:scale-105"
          @click="openLightbox(currentIndex)"
          loading="lazy"
        />
        <!-- Image Counter -->
        <div class="absolute top-4 right-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm">
          {{ currentIndex + 1 }} / {{ images.length }}
        </div>
        <!-- Navigation Arrows on Main Image -->
        <button 
          v-if="images.length > 1"
          @click="prevImage" 
          class="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          aria-label="Previous image"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button 
          v-if="images.length > 1"
          @click="nextImage" 
          class="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          aria-label="Next image"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <!-- Caption -->
      <div v-if="images[currentIndex].caption" class="mt-3 text-center text-gray-700 text-sm md:text-base">
        {{ images[currentIndex].caption }}
      </div>
    </div>

    <!-- Hidden Thumbnail Navigation for Dots -->
    <div v-if="images.length > 1" class="flex justify-center mt-4 space-x-2">
      <button
        v-for="(image, index) in images"
        :key="`dot-${index}`"
        @click="setCurrentImage(index)"
        class="w-3 h-3 rounded-full transition-all duration-300"
        :class="index === currentIndex ? 'bg-blue-500' : 'bg-gray-300 hover:bg-gray-400'"
        :aria-label="`Go to image ${index + 1}`"
      ></button>
    </div>

    <!-- Custom Lightbox Modal -->
    <div 
      v-if="lightboxOpen" 
      class="lightbox-overlay fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
      @click="closeLightbox"
      @keydown.esc="closeLightbox"
    >
      <div class="lightbox-container relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
        <!-- Close Button -->
        <button 
          @click="closeLightbox"
          class="absolute top-4 right-4 z-10 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-3 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          aria-label="Close lightbox"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Navigation Arrows -->
        <button 
          v-if="images.length > 1"
          @click.stop="prevImageLightbox" 
          class="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-3 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          aria-label="Previous image"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <button 
          v-if="images.length > 1"
          @click.stop="nextImageLightbox" 
          class="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-3 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          aria-label="Next image"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <!-- Main Lightbox Image -->
        <div class="lightbox-image-container flex flex-col items-center justify-center max-w-full max-h-full" @click.stop>
          <img 
            :src="images[lightboxIndex].largeURL" 
            :alt="images[lightboxIndex].caption || `Image ${lightboxIndex + 1}`"
            class="lightbox-image max-w-full max-h-[80vh] w-auto h-auto object-contain"
            @load="onLightboxImageLoad"
          />
          
          <!-- Image Info -->
          <div class="mt-4 text-center text-white">
            <div class="text-lg font-semibold mb-2">{{ lightboxIndex + 1 }} / {{ images.length }}</div>
            <div v-if="images[lightboxIndex].caption" class="text-sm md:text-base max-w-2xl">
              {{ images[lightboxIndex].caption }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GalleryApp',
  props: {
    images: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      currentIndex: 0,
      lightboxOpen: false,
      lightboxIndex: 0,
    };
  },
  mounted() {
    // Add keyboard event listeners
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeUnmount() {
    // Clean up event listeners
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    // Main gallery navigation
    nextImage() {
      this.currentIndex = this.currentIndex < this.images.length - 1 ? this.currentIndex + 1 : 0;
    },
    prevImage() {
      this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.images.length - 1;
    },
    setCurrentImage(index) {
      this.currentIndex = index;
    },

    // Lightbox methods
    openLightbox(index) {
      this.lightboxIndex = index;
      this.lightboxOpen = true;
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    },
    closeLightbox() {
      this.lightboxOpen = false;
      document.body.style.overflow = ''; // Restore scroll
    },
    nextImageLightbox() {
      this.lightboxIndex = (this.lightboxIndex + 1) % this.images.length;
    },
    prevImageLightbox() {
      this.lightboxIndex = this.lightboxIndex > 0 ? this.lightboxIndex - 1 : this.images.length - 1;
    },
    onLightboxImageLoad() {
      // Image loaded successfully
    },

    // Keyboard navigation
    handleKeydown(event) {
      if (!this.lightboxOpen) return;
      
      switch (event.key) {
        case 'Escape':
          this.closeLightbox();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.prevImageLightbox();
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.nextImageLightbox();
          break;
      }
    },
  },
};
</script>

<style scoped>
.gallery-container {
  width: 100%;
  max-width: 64rem;
  margin: 0 auto;
}

.main-image-container {
  position: relative;
}

.main-image {
  transition: transform 0.3s ease;
}

.lightbox-overlay {
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease-out;
}

.lightbox-image {
  animation: zoomIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .lightbox-image {
    max-height: 70vh;
  }
  
  .main-image {
    max-height: 300px;
  }
}

@media (max-width: 480px) {
  .main-image {
    max-height: 250px;
  }
}
</style>
